/**
 * Custom email-action handler for Firebase Auth email verification.
 *
 * Lives at `/verify-email`. The Firebase Console's "Customize action
 * URL" for the email-verification template is set to
 * `https://app.greenbroad.co/verify-email` so the link in the
 * verification email lands here instead of the default
 * `firebaseapp.com/__/auth/action` page.
 *
 * Flow:
 *   1. Email arrives → user clicks link → browser opens
 *      `/verify-email?mode=verifyEmail&oobCode=...&continueUrl=...`
 *   2. We mount, parse URL params, call `applyActionCode(auth, oobCode)`
 *   3. Success: show ✓ tile, redirect to `continueUrl` after 3s (or
 *      user clicks "Continue →")
 *   4. Errors: show actionable copy (expired, invalid, network)
 *
 * `applyActionCode` does NOT require an active session — the code
 * itself authenticates. So the link works from any browser/device,
 * not just the one the user signed up in. After verification the
 * user is sent to /intake, where AuthGate handles sign-in if needed.
 *
 * Unsupported modes (resetPassword, recoverEmail) render an
 * informative message — those flows aren't implemented yet, but the
 * handler must not crash when the Console templates inevitably get
 * pointed here too.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type Auth, applyActionCode } from 'firebase/auth';

import { tryGetFirebaseRuntime } from '../firebase/init';
import { reportError, reportEvent } from '../monitoring';
import styles from './VerifyEmailHandlerPage.module.css';

export type TVerifyEmailStatus =
  | 'loading'
  | 'success'
  | 'error_expired'
  | 'error_invalid'
  | 'error_network'
  | 'error_unsupported_mode'
  | 'error_runtime_missing'
  | 'error_unknown';

const REDIRECT_DELAY_MS = 3000;

export function mapFirebaseError(err: unknown): TVerifyEmailStatus {
  const code = (err as { code?: string } | null)?.code ?? '';
  if (code === 'auth/expired-action-code') return 'error_expired';
  if (
    code === 'auth/invalid-action-code' ||
    code === 'auth/user-disabled' ||
    code === 'auth/user-not-found'
  ) {
    return 'error_invalid';
  }
  if (code === 'auth/network-request-failed') return 'error_network';
  return 'error_unknown';
}

/**
 * Sanitize the `continueUrl` query param down to a same-origin path.
 * Defends against an attacker injecting an off-domain redirect by
 * tampering with the link (the oobCode is signed but the continueUrl
 * isn't). Returns `/intake` as the safe fallback.
 */
export function resolveContinueRoute(
  continueUrl: string | null,
  currentOrigin: string,
): string {
  if (continueUrl === null || continueUrl.length === 0) return '/intake';
  try {
    const url = new URL(continueUrl, currentOrigin);
    if (url.origin !== currentOrigin) return '/intake';
    const pathPart = url.pathname || '/intake';
    return `${pathPart}${url.search}${url.hash}`;
  } catch {
    return '/intake';
  }
}

interface IVerifyEmailHandlerPageProps {
  /**
   * Optional Firebase Auth instance — supplied by tests. When omitted
   * the page falls back to `tryGetFirebaseRuntime()` (the production
   * path). Keeping this seam means the test never touches the real
   * Firebase SDK.
   */
  authOverride?: Auth;
  /** Overrides `window.location.origin` for tests. */
  originOverride?: string;
}

export function VerifyEmailHandlerPage(
  props: IVerifyEmailHandlerPageProps = {},
): JSX.Element {
  const { authOverride, originOverride } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TVerifyEmailStatus>('loading');

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrlParam = searchParams.get('continueUrl');

  const origin = useMemo(
    () =>
      originOverride ??
      (typeof window !== 'undefined'
        ? window.location.origin
        : 'https://app.greenbroad.co'),
    [originOverride],
  );

  const continueRoute = useMemo(
    () => resolveContinueRoute(continueUrlParam, origin),
    [continueUrlParam, origin],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      if (mode === null || mode === 'verifyEmail') {
        if (oobCode === null || oobCode.length === 0) {
          setStatus('error_invalid');
          return;
        }
        let auth: Auth;
        if (authOverride !== undefined) {
          auth = authOverride;
        } else {
          const runtime = tryGetFirebaseRuntime();
          if (!runtime.ok) {
            setStatus('error_runtime_missing');
            return;
          }
          auth = runtime.runtime.auth;
        }
        reportEvent('auth.verify_email_link_opened');
        try {
          await applyActionCode(auth, oobCode);
          if (!cancelled) {
            setStatus('success');
            reportEvent('auth.verify_email_handler_success');
          }
        } catch (err) {
          const mapped = mapFirebaseError(err);
          if (!cancelled) setStatus(mapped);
          // Always emit a breadcrumb (so Sentry sees the failure
          // funnel). Reserve a full error report for the unknown
          // branch — expired/invalid/network are user-actionable
          // and noisy if we ship every one to Sentry as an error.
          reportEvent('auth.verify_email_handler_failed', { mapped });
          if (mapped === 'error_unknown') {
            reportError(err, {
              kind: 'auth',
              flow: 'verify_email_handler',
            });
          }
        }
        return;
      }
      // mode is set but not 'verifyEmail' — resetPassword / recoverEmail
      // / something USCIS-Auth-future. We don't handle those flows yet.
      setStatus('error_unsupported_mode');
      reportEvent('auth.verify_email_handler_unsupported_mode', { mode });
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, authOverride]);

  // Auto-redirect on success.
  useEffect(() => {
    if (status !== 'success') return;
    const id = setTimeout(() => {
      navigate(continueRoute, { replace: true });
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(id);
  }, [status, continueRoute, navigate]);

  return (
    <main
      id="main-content"
      className={styles.shell}
      aria-labelledby="verify-title"
    >
      <header className={styles.topbar}>
        <img
          src="/brand/greenbroad-wordmark.png"
          alt="Greenbroad"
          className={styles.topbar__brand}
          width={140}
          height={29}
        />
        <span className={styles.topbar__meta}>Email verification</span>
      </header>

      <section className={styles.container}>
        <div className={styles.card} data-verify-status={status}>
          {status === 'loading' ? (
            <>
              <div className={styles.spinner} aria-hidden="true" />
              <h1 id="verify-title" className={styles.title}>
                Verifying your email…
              </h1>
              <p className={styles.body}>
                Hang tight — this takes a second.
              </p>
            </>
          ) : null}

          {status === 'success' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--success']}`}
                aria-hidden="true"
              >
                ✓
              </div>
              <h1 id="verify-title" className={styles.title}>
                Email verified
              </h1>
              <p className={styles.body}>
                Thanks for confirming. We're redirecting you to your
                application now.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => navigate(continueRoute, { replace: true })}
              >
                Continue to your application →
              </button>
            </>
          ) : null}

          {status === 'error_expired' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--warning']}`}
                aria-hidden="true"
              >
                !
              </div>
              <h1 id="verify-title" className={styles.title}>
                This link has expired
              </h1>
              <p className={styles.body}>
                For your security, verification links expire after a short
                time. Sign in and we'll send you a fresh one.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => navigate('/sign-up', { replace: true })}
              >
                Sign in to get a new link
              </button>
            </>
          ) : null}

          {status === 'error_invalid' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--warning']}`}
                aria-hidden="true"
              >
                !
              </div>
              <h1 id="verify-title" className={styles.title}>
                This link isn't valid
              </h1>
              <p className={styles.body}>
                The link may have been already used, or the verification
                code is malformed. Make sure you clicked the link in the
                most recent verification email we sent.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => navigate('/sign-up', { replace: true })}
              >
                Sign in and resend
              </button>
            </>
          ) : null}

          {status === 'error_network' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--warning']}`}
                aria-hidden="true"
              >
                !
              </div>
              <h1 id="verify-title" className={styles.title}>
                Couldn't reach our servers
              </h1>
              <p className={styles.body}>
                A network hiccup got in the way. Check your connection and
                try again.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </>
          ) : null}

          {status === 'error_unsupported_mode' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--neutral']}`}
                aria-hidden="true"
              >
                ?
              </div>
              <h1 id="verify-title" className={styles.title}>
                This link type isn't supported yet
              </h1>
              <p className={styles.body}>
                Email action mode <code>{mode}</code> isn't handled by
                Greenbroad. If you reached here unexpectedly, sign in and
                we'll get you back on track.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => navigate('/sign-up', { replace: true })}
              >
                Sign in
              </button>
            </>
          ) : null}

          {status === 'error_runtime_missing' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--warning']}`}
                aria-hidden="true"
              >
                !
              </div>
              <h1 id="verify-title" className={styles.title}>
                We can't verify right now
              </h1>
              <p className={styles.body}>
                Our auth system isn't reachable. This is unusual — please
                try again in a minute.
              </p>
            </>
          ) : null}

          {status === 'error_unknown' ? (
            <>
              <div
                className={`${styles.icon} ${styles['icon--warning']}`}
                aria-hidden="true"
              >
                !
              </div>
              <h1 id="verify-title" className={styles.title}>
                Something went wrong
              </h1>
              <p className={styles.body}>
                We couldn't verify your email. Sign in and we'll send you a
                new link to try again.
              </p>
              <button
                type="button"
                className={styles.primary_cta}
                onClick={() => navigate('/sign-up', { replace: true })}
              >
                Sign in
              </button>
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
}
